/* Copyright (c) 2020-present CloudIO Inc */

import { makeStyles } from '@mui/styles';
import cx from 'clsx';
import React, { memo, useEffect, useState } from 'react';
import { ItemID, AnyColumn } from 'componentregistry';
import { alpha, Direction, darken, lighten, Theme } from '@mui/material';
import Cells from './Cells';
import { Cell, CellComponentProps, DataContext } from './types';
import { useGridBounds } from '../Context';

const useStyles = makeStyles((theme: Theme) => ({
  gridFooter: {
    position: 'absolute',
    overflow: 'hidden',
    bottom: 0,
    borderTop: '1px solid',
    borderTopColor:
      theme.palette.mode === 'light'
        ? lighten(alpha(theme.palette.divider, 1), 0.88)
        : darken(alpha(theme.palette.divider, 1), 0.68),
  },
  gridFooterCenter: {
    zIndex: 3,
  },
  gridFooterLeft: {
    zIndex: 4,
    backgroundColor: theme.palette.background.paper,
  },
  gridFooterRight: {
    zIndex: 4,
    backgroundColor: theme.palette.background.paper,
  },
}));

interface Props {
  CellComponent: React.FunctionComponent<CellComponentProps>;
  appUid: string;
  roleUid: string;
  columnLefts: number[];
  columnWidths: number[];
  columns: AnyColumn[];
  custom: boolean;
  dataContext: DataContext;
  direction?: Direction;
  fixedLeftColumns: number;
  fixedRightColumns: number;
  footerRows: number;
  innerWidth: number;
  onClick?: (e: React.MouseEvent<HTMLElement>, cell: Cell<AnyColumn>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  pageId: ItemID;
  resetScroll: () => void;
  rowHeight: number;
  showColumnLines: boolean;
  showRowLines: boolean;
  totalColumns: number;
  totalRows: number;
  vertialScrollbarSize: number;
  // eslint-disable-next-line react/no-unused-prop-types
  width: number;
}

function Footer(props: Props): React.ReactElement {
  const classes = useStyles();
  const {
    CellComponent,
    appUid,
    roleUid,
    columnLefts,
    columnWidths,
    columns,
    custom,
    dataContext,
    direction,
    fixedLeftColumns,
    fixedRightColumns,
    footerRows,
    innerWidth,
    onClick,
    onKeyDown,
    pageId,
    resetScroll,
    rowHeight,
    showColumnLines,
    showRowLines,
    totalColumns,
    totalRows,
    vertialScrollbarSize,
  } = props;

  const leftFixedWidth = fixedLeftColumns
    ? columnLefts[fixedLeftColumns - 1] + columnWidths[fixedLeftColumns - 1]
    : 0;
  const rightFixedWidth = fixedRightColumns
    ? innerWidth - columnLefts[totalColumns - fixedRightColumns]
    : 0;

  const footerHeight = footerRows ? footerRows * rowHeight : 0;

  const gridBoundsSignal = useGridBounds();
  const [{ left, startColumn, endColumn, showShadow }, setState] = useState<{
    left: number;
    startColumn: number;
    endColumn: number;
    showShadow: boolean;
  }>(() => {
    const bound = gridBoundsSignal.peek();
    return {
      left: bound.pos.left,
      startColumn: bound.bound.column.start,
      endColumn: bound.bound.column.end,
      endRow: bound.bound.row.end,
      showShadow: bound.pos.top > 5,
    };
  });

  useEffect(() => {
    return gridBoundsSignal.pos.onChange(({ left: _left, top }) => {
      setState((s) =>
        s.left === _left ? s : { ...s, left: _left, showShadow: top > 5 },
      );
    });
  }, [gridBoundsSignal]);
  useEffect(() => {
    return gridBoundsSignal.bound.column.onChange((column) => {
      setState((s) => ({
        ...s,
        startColumn: column.start,
        endColumn: column.end,
      }));
    });
  }, [gridBoundsSignal]);

  return (
    <>
      <div
        className={cx(
          classes.gridFooter,
          classes.gridFooterCenter,
          showShadow ? 'io-grid-footer-shadow' : null,
        )}
        style={{
          height: footerHeight,
          width: innerWidth,
          [direction === 'rtl' ? 'right' : 'left']: leftFixedWidth + -1 * left,
          bottom: 0,
        }}
      >
        <Cells
          CellComponent={CellComponent}
          appUid={appUid}
          roleUid={roleUid}
          columnLefts={columnLefts}
          columnWidths={columnWidths}
          columns={columns}
          custom={custom}
          dataContext={dataContext}
          direction={direction}
          endColumn={endColumn}
          endRow={totalRows - 1}
          fixedLeftColumns={fixedLeftColumns}
          onClick={onClick}
          onKeyDown={onKeyDown}
          pageId={pageId}
          region="footer-center"
          resetScroll={resetScroll}
          rowHeight={rowHeight}
          showColumnLines={showColumnLines}
          showRowLines={showRowLines}
          startColumn={startColumn}
          startRow={totalRows - footerRows}
          actualEndRow={totalRows - 1}
          actualStartRow={totalRows - footerRows}
          totalRows={totalRows}
        />
      </div>
      {fixedLeftColumns > 0 ? (
        <div
          className={cx(
            classes.gridFooter,
            classes.gridFooterLeft,
            showShadow ? 'io-grid-footer-left-fixed-shadow' : null,
          )}
          style={{
            height: footerHeight,
            width: leftFixedWidth,
            [direction === 'rtl' ? 'right' : 'left']: 0,
          }}
        >
          <Cells
            CellComponent={CellComponent}
            appUid={appUid}
            roleUid={roleUid}
            columnLefts={columnLefts}
            columnWidths={columnWidths}
            columns={columns}
            custom={custom}
            dataContext={dataContext}
            direction={direction}
            endColumn={fixedLeftColumns - 1}
            endRow={totalRows - 1}
            fixedLeftColumns={fixedLeftColumns}
            onClick={onClick}
            onKeyDown={onKeyDown}
            pageId={pageId}
            region="footer-left"
            resetScroll={resetScroll}
            rowHeight={rowHeight}
            showColumnLines={showColumnLines}
            showRowLines={showRowLines}
            startColumn={0}
            startRow={totalRows - footerRows}
            actualEndRow={totalRows - 1}
            actualStartRow={totalRows - footerRows}
            totalRows={totalRows}
          />
        </div>
      ) : null}
      {fixedRightColumns > 0 ? (
        <div
          className={cx(
            classes.gridFooter,
            classes.gridFooterRight,
            showShadow ? 'io-grid-footer-right-fixed-shadow' : null,
          )}
          style={{
            height: footerHeight,
            width: rightFixedWidth + vertialScrollbarSize,
            [direction === 'rtl' ? 'left' : 'right']: 0,
          }}
        >
          <Cells
            CellComponent={CellComponent}
            appUid={appUid}
            roleUid={roleUid}
            columnWidths={columnWidths}
            columns={columns}
            custom={custom}
            dataContext={dataContext}
            direction={direction}
            endColumn={totalColumns - 1}
            endRow={totalRows - 1}
            fixedLeftColumns={fixedLeftColumns}
            onClick={onClick}
            onKeyDown={onKeyDown}
            pageId={pageId}
            region="footer-right"
            resetScroll={resetScroll}
            rowHeight={rowHeight}
            showColumnLines={showColumnLines}
            showRowLines={showRowLines}
            startColumn={totalColumns - fixedRightColumns}
            startRow={totalRows - footerRows}
            actualEndRow={totalRows - 1}
            actualStartRow={totalRows - footerRows}
            totalRows={totalRows}
          />
        </div>
      ) : null}
    </>
  );
}

Footer.displayName = 'Footer';

export default memo<Props>(Footer);
